//src/pages/gro/WorkerGroupsPage.tsx
import { useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Search, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { CrudRowActions } from "@/components/shared/CrudRowActions";
import { EmptyState } from "@/components/shared/EmptyState";
import { PageHeader } from "@/components/shared/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  useRefreshWorkerGroup,
  useSetWorkerGroupStatus,
  useWorkerGroups,
} from "@/hooks/useWorkerGroups";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import type {
  WorkerGroup,
  WorkerGroupListParams,
  WorkerGroupType,
} from "@/types/gro";

function groupTypeLabel(value: WorkerGroupType) {
  return value === "dynamic" ? "Dinâmico" : "Manual";
}

export default function WorkerGroupsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const canRead =
    user?.permissions?.includes(PERMISSIONS.WORKER_GROUPS_READ) ?? false;
  const canWrite =
    user?.permissions?.includes(PERMISSIONS.WORKER_GROUPS_WRITE) ?? false;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [groupTypeFilter, setGroupTypeFilter] = useState<WorkerGroupType | "all">("all");
  const [includeInactive, setIncludeInactive] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedToggle, setSelectedToggle] = useState<WorkerGroup | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const params: WorkerGroupListParams = useMemo(
    () => ({
      include_inactive: includeInactive,
      q: debouncedSearch,
      group_type: groupTypeFilter === "all" ? "" : groupTypeFilter,
    }),
    [debouncedSearch, groupTypeFilter, includeInactive],
  );

  const listQ = useWorkerGroups(params);
  const refreshMut = useRefreshWorkerGroup();
  const setStatusMut = useSetWorkerGroupStatus();

  const items = listQ.data ?? [];

  async function handleRefresh(id: string) {
    try {
      await refreshMut.mutateAsync(id);
      toast.success("Grupo recalculado com sucesso.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao recalcular grupo.");
    }
  }

  async function handleConfirmToggle() {
    if (!selectedToggle) return;

    try {
      await setStatusMut.mutateAsync({
        id: selectedToggle.id,
        is_active: !selectedToggle.is_active,
      });

      toast.success(
        selectedToggle.is_active ? "Grupo inativado com sucesso." : "Grupo ativado com sucesso.",
      );
      setConfirmOpen(false);
      setSelectedToggle(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status do grupo.");
    }
  }

  return (
    <div className="pb-10">
      <PageHeader
        title="Grupos de Avaliação"
        description="Crie e mantenha grupos manuais ou dinâmicos para campanhas e análises psicossociais."
      >
        {canWrite ? (
          <Button onClick={() => navigate("/gro/worker-groups/novo")}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Grupo
          </Button>
        ) : null}
      </PageHeader>

      <Card className="mb-4 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <Select
            value={groupTypeFilter}
            onValueChange={(value) =>
              setGroupTypeFilter(value as WorkerGroupType | "all")
            }
          >
            <SelectTrigger className="lg:w-[180px]">
              <SelectValue placeholder="Todos os tipos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
              <SelectItem value="dynamic">Dinâmico</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={includeInactive ? "all" : "active"}
            onValueChange={(value) => setIncludeInactive(value === "all")}
          >
            <SelectTrigger className="lg:w-[180px]">
              <SelectValue placeholder="Somente ativos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Somente ativos</SelectItem>
              <SelectItem value="all">Ativos e inativos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {!canRead ? (
        <Card className="p-6 text-sm text-destructive">
          Você não possui permissão para visualizar grupos de avaliação.
        </Card>
      ) : listQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando grupos...
        </Card>
      ) : listQ.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(listQ.error as any)?.message || "Erro ao carregar grupos"}
          </div>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum grupo encontrado"
          description="Crie o primeiro grupo para segmentar campanhas e análises."
        />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Grupo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Membros</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Atualizado em</TableHead>
                <TableHead className="w-[220px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{item.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {item.description || "Sem descrição"}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <Badge variant={item.group_type === "dynamic" ? "default" : "secondary"}>
                      {groupTypeLabel(item.group_type)}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      {item.member_count}
                    </div>
                  </TableCell>

                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "secondary"}>
                      {item.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {item.updated_at || "-"}
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canWrite ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Recalcular grupo"
                          onClick={() => handleRefresh(item.id)}
                          disabled={refreshMut.isPending}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      ) : null}

                      {canWrite ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedToggle(item);
                            setConfirmOpen(true);
                          }}
                        >
                          {item.is_active ? "Inativar" : "Ativar"}
                        </Button>
                      ) : null}

                      <CrudRowActions
                        onView={() => navigate(`/gro/worker-groups/${item.id}`)}
                        onEdit={
                          canWrite
                            ? () => navigate(`/gro/worker-groups/${item.id}/editar`)
                            : undefined
                        }
                        viewPermission={PERMISSIONS.WORKER_GROUPS_READ}
                        editPermission={PERMISSIONS.WORKER_GROUPS_WRITE}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={selectedToggle?.is_active ? "Inativar grupo" : "Ativar grupo"}
        description={
          selectedToggle
            ? `Deseja ${selectedToggle.is_active ? "inativar" : "ativar"} o grupo "${selectedToggle.name}"?`
            : ""
        }
        onConfirm={handleConfirmToggle}
        confirmLabel={selectedToggle?.is_active ? "Inativar" : "Ativar"}
      />
    </div>
  );
}
